import { useEffect, useState } from "react";
import {
  CREATURE_PAGE,
  type CreatureList,
  type CreatureRow,
  type CreatureSort,
  type LibraryHit,
} from "@ai-rpg/protocol";
import { listCreatures } from "../api.ts";
import { StatBlockView } from "./LibraryPicker.tsx";
import { LOADING } from "../words.ts";

const SEARCH_DELAY_MS = 250;
/** Challenge ratings to narrow by: the fractions, then every whole number. */
const RATINGS = [0, 0.125, 0.25, 0.5, ...Array.from({ length: 30 }, (_, i) => i + 1)];
const rating = (cr: number | null) =>
  cr === null ? "—" : cr === 0.125 ? "1/8" : cr === 0.25 ? "1/4" : cr === 0.5 ? "1/2" : String(cr);
const COLUMNS: readonly { readonly sort: CreatureSort; readonly label: string }[] = [
  { sort: "name", label: "Name" },
  { sort: "cr", label: "CR" },
  { sort: "type", label: "Kind" },
  { sort: "book", label: "Book" },
];

interface Props {
  readonly label: string;
  /** What happens when nothing is chosen. */
  readonly note: string;
  readonly max: number;
  /** The rules the game will play: decides which printing of a creature is offered. */
  readonly edition: "2014" | "2024";
  readonly chosen: readonly LibraryHit[];
  readonly onChange: (chosen: LibraryHit[]) => void;
}

/**
 * The creatures of the player's books as a table: narrowed by name,
 * challenge rating and kind, sorted by any column, in a frame that scrolls
 * rather than a page that grows. The same creature may be chosen more than
 * once.
 */
export function CreatureGrid({ label, note, max, edition, chosen, onChange }: Props) {
  const [q, setQ] = useState("");
  const [crMin, setCrMin] = useState("");
  const [crMax, setCrMax] = useState("");
  const [type, setType] = useState("");
  const [sort, setSort] = useState<CreatureSort>("name");
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  const [list, setList] = useState<CreatureList | null>(null);
  const [more, setMore] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [opened, setOpened] = useState<CreatureRow | null>(null);
  const full = chosen.length >= max;

  const query = (offset: number) => ({
    edition,
    sort,
    dir,
    offset,
    ...(q.trim() ? { q: q.trim() } : {}),
    ...(crMin !== "" ? { crMin: Number(crMin) } : {}),
    ...(crMax !== "" ? { crMax: Number(crMax) } : {}),
    ...(type ? { type } : {}),
  });

  useEffect(() => {
    let stale = false;
    const timer = setTimeout(() => {
      listCreatures(query(0))
        .then((found) => {
          if (stale) return;
          setList(found);
          setProblem(null);
        })
        .catch((err: unknown) => !stale && setProblem(err instanceof Error ? err.message : String(err)));
    }, SEARCH_DELAY_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
  }, [edition, q, crMin, crMax, type, sort, dir]);

  const showMore = async () => {
    if (!list || more) return;
    setMore(true);
    try {
      const next = await listCreatures(query(list.rows.length));
      setList({ ...next, rows: [...list.rows, ...next.rows] });
    } catch (err) {
      setProblem(err instanceof Error ? err.message : String(err));
    } finally {
      setMore(false);
    }
  };

  const sortBy = (column: CreatureSort) => {
    if (column === sort) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(column);
      setDir("asc");
    }
  };
  const add = (row: CreatureRow) => {
    if (full) return;
    const { key, name, bookId, book, detail } = row;
    onChange([...chosen, { key, name, bookId, book, detail }]);
  };

  return (
    <div className="field">
      <span className="field-label">
        {label}
        <span className="section-aside">
          {" "}
          {chosen.length} of {max}
        </span>
      </span>

      {chosen.length > 0 ? (
        <ul className="picked">
          {chosen.map((c, i) => (
            <li key={`${c.key}-${i}`} className="picked-row">
              <span className="hit-name">{c.name}</span>
              <span className="hit-detail">{c.detail}</span>
              <span className="hit-book">{c.book}</span>
              <button type="button" className="link" onClick={() => onChange(chosen.filter((_, at) => at !== i))}>
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="field-note">{note}</p>
      )}

      <div className="grid-filters">
        <label className="field">
          <span className="field-label">Name</span>
          <input
            className="input"
            type="search"
            value={q}
            maxLength={60}
            autoComplete="off"
            placeholder="Any"
            onChange={(e) => setQ(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">CR from</span>
          <select className="input" value={crMin} onChange={(e) => setCrMin(e.target.value)}>
            <option value="">Any</option>
            {RATINGS.map((r) => (
              <option key={r} value={r}>
                {rating(r)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">CR to</span>
          <select className="input" value={crMax} onChange={(e) => setCrMax(e.target.value)}>
            <option value="">Any</option>
            {RATINGS.map((r) => (
              <option key={r} value={r}>
                {rating(r)}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="field-label">Kind</span>
          <select className="input" value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">Any</option>
            {list?.types.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </label>
      </div>
      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}

      <div className="grid-frame" tabIndex={0} aria-label={`${label}: creatures to choose from`}>
        <table className="grid grid-pick">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th key={c.sort} aria-sort={sort === c.sort ? (dir === "asc" ? "ascending" : "descending") : "none"}>
                  <button type="button" className="grid-sort" data-on={sort === c.sort} onClick={() => sortBy(c.sort)}>
                    {c.label}
                    <span aria-hidden="true">{sort === c.sort ? (dir === "asc" ? " ↑" : " ↓") : ""}</span>
                  </button>
                </th>
              ))}
              <th>Size</th>
              <th>
                <span className="visually-hidden">Choose</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list?.rows.map((r) => (
              <tr key={r.key} data-on={opened?.key === r.key}>
                <th scope="row">{r.name}</th>
                <td className="figures">{rating(r.cr)}</td>
                <td>{r.type ?? "—"}</td>
                <td className="grid-quiet">{r.book}</td>
                <td>{r.size ?? "—"}</td>
                <td className="grid-actions">
                  {list.text && (
                    <button
                      type="button"
                      className="link"
                      aria-expanded={opened?.key === r.key}
                      onClick={() => setOpened(opened?.key === r.key ? null : r)}
                    >
                      Stats
                    </button>
                  )}
                  <button type="button" className="link" disabled={full} onClick={() => add(r)}>
                    Add
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {list && list.rows.length === 0 && <p className="quiet grid-none">Nothing in your books fits that.</p>}
        {list && list.rows.length < list.total && (
          <p className="grid-more">
            <button type="button" className="link" disabled={more} onClick={() => void showMore()}>
              {more ? LOADING : `Show ${Math.min(CREATURE_PAGE, list.total - list.rows.length)} more`}
            </button>
          </p>
        )}
      </div>
      <p className="field-note figures">
        {list === null
          ? LOADING
          : `${list.rows.length} of ${list.total} shown${full ? " · that's as many as fit" : ""}`}
      </p>

      {opened && (
        <div className="grid-opened">
          <p className="field-note">
            <strong>{opened.name}</strong>, from {opened.book}.{" "}
            <button type="button" className="link" onClick={() => setOpened(null)}>
              Close
            </button>
          </p>
          <StatBlockView hit={opened} edition={edition} />
        </div>
      )}
    </div>
  );
}
